import * as React from "react";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

export const ScopeModeConst = {
  ALL: "ALL",
  PARTNER: "PARTNER",
  ORG: "ORG",
} as const;

export type ScopeMode = (typeof ScopeModeConst)[keyof typeof ScopeModeConst];

/** Dashboard landing paths per scope — single source of truth for URL-to-scope mapping */
export const SCOPE_DASHBOARD_PATHS: Record<ScopeMode, string> = {
  [ScopeModeConst.ALL]: "/system_dashboard",
  [ScopeModeConst.PARTNER]: "/partners_dashboard",
  [ScopeModeConst.ORG]: "/organization",
};

export const isDashboardPath = (pathname: string): boolean =>
  Object.values(SCOPE_DASHBOARD_PATHS).some((p) => pathname.startsWith(p));

const getScopeModeFromPath = (pathname: string, hasOrgId: boolean): ScopeMode => {
  for (const [scope, path] of Object.entries(SCOPE_DASHBOARD_PATHS) as [ScopeMode, string][]) {
    if (pathname.startsWith(path)) return scope;
  }
  if (hasOrgId) return ScopeModeConst.ORG;
  return ScopeModeConst.ALL;
};

interface OrgContextValue {
  orgId: number | null;
  updateOrg: (newOrgId: number | null) => void;
  scopeMode: ScopeMode;
  /**
   * Append the currently selected org to a link so navigating to it keeps the sidebar's
   * org context. Picks `?`/`&` automatically and is a no-op when no org is selected.
   * Use for links that should stay in the current org (STIC-15687). For a link that must
   * carry a *different* org, build the URL with that org explicitly instead.
   */
  withOrg: (url: string) => string;
}

const OrgContext = React.createContext<OrgContextValue | null>(null);

interface OrgProviderProps {
  children: React.ReactNode;
}

export const OrgProvider = ({ children }: OrgProviderProps) => {
  const location = useLocation();
  const history = useHistory();

  // Derive scope mode synchronously from the URL
  const scopeMode = useMemo(
    () => getScopeModeFromPath(location.pathname, new URLSearchParams(location.search).has("org")),
    [location.pathname, location.search]
  );

  const getOrgIdFromLocation = useCallback((loc: typeof location) => {
    const param = new URLSearchParams(loc.search);
    const org = param.get("org");
    return org === null ? null : isNaN(Number(org)) ? null : parseInt(org);
  }, []);

  const updateOrg = (newOrgId: number | null) => {
    const param = new URLSearchParams(location.search);
    const oldOrgId = getOrgIdFromLocation(location);
    if (oldOrgId !== newOrgId) {
      if (!newOrgId) {
        param.delete("org");
      } else {
        param.set("org", String(newOrgId));
      }
      history.replace({
        search: param.toString(),
      });
    }
  };

  const orgId = getOrgIdFromLocation(location);

  const withOrg = useCallback(
    (url: string) => (orgId ? `${url}${url.includes("?") ? "&" : "?"}org=${orgId}` : url),
    [orgId]
  );

  return (
    <OrgContext.Provider
      value={{ orgId, updateOrg, scopeMode, withOrg }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextValue => {
  const context = React.useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
};
